// File: src/features/jobs/useMcpJobAnalysis.ts
// MCP Job Analysis Hook - Phase 2 Implementation
//
// ✅ PROVIDES: Job failure analysis using MCP orchestration
// ✅ ENABLES: Real-time MCP analysis results and tracking
// ✅ INTEGRATES: Seamlessly with existing job monitoring

import { useState, useCallback, useEffect, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import { useAppStore } from '../../store/appStore';
import {
  analyzeJob,
  analyzeJobsBatch,
  getMcpJobData,
  type JobAnalysisRequest,
  type JobAnalysisResult,
  type BatchAnalysisRequest,
  type BatchAnalysisResult,
  type McpJobData
} from './mcpApi';

// ===== INTERFACES =====

interface McpJobAnalysisState {
  mcpJobsData: Record<string, McpJobData>;
  analysisInProgress: Set<string>;
  batchAnalysisInProgress: boolean;
  error: string | null;
  lastUpdated: string | null;
}

interface UseMcpJobAnalysisResult {
  // State
  mcpJobs: Readonly<Record<string, McpJobData>>;
  isAnalyzing: boolean;
  isBatchAnalyzing: boolean;
  error: string | null;
  
  // Analysis Operations
  analyzeFailedJob: (jobId: string, options?: Partial<JobAnalysisRequest>) => Promise<JobAnalysisResult>;
  analyzeFailedJobs: (jobIds: string[], options?: Partial<BatchAnalysisRequest>) => Promise<BatchAnalysisResult>;
  refreshAnalysisData: (jobIds: string[]) => Promise<void>;
  
  // Data Access
  hasAnalysis: (jobId: string) => boolean;
  getAnalysis: (jobId: string) => JobAnalysisResult | null;
  getAnalysisIndicator: (jobId: string) => '🧠' | '✨' | '⚠️' | '❌' | null;
  
  // 🚀 NEW: Enhanced Data Accessors (Phase 2)
  getLastAnalyzedAt: (jobId: string) => string | null;
  getAnalysisStatus: (jobId: string) => 'success' | 'warning' | 'error' | 'unknown';
  getAnalysisSummary: (jobId: string) => string | null;
  isAnalysisStale: (jobId: string, maxAgeMinutes?: number) => boolean;
  
  // Utilities
  clearError: () => void;
}

// ===== HOOK IMPLEMENTATION =====

export function useMcpJobAnalysis(): UseMcpJobAnalysisResult {
  const { keycloak } = useKeycloak();
  const { currentFirmId } = useAppStore();
  
  // 🚀 NEW: Interval ref for safer auto-refresh (Phase 2)
  const autoRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  const [state, setState] = useState<McpJobAnalysisState>({
    mcpJobsData: {},
    analysisInProgress: new Set(),
    batchAnalysisInProgress: false,
    error: null,
    lastUpdated: null
  });

  // ===== ERROR HANDLING =====
  
  const handleError = useCallback((error: unknown, operation: string) => {
    const errorMessage = error instanceof Error ? error.message : `${operation} failed`;
    console.error(`[useMcpJobAnalysis] ${operation} failed:`, error);
    setState(prev => ({ ...prev, error: errorMessage }));
  }, []);

  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  // ===== ANALYSIS OPERATIONS =====

  const analyzeFailedJob = useCallback(async (
    jobId: string, 
    options: Partial<JobAnalysisRequest> = {}
  ): Promise<JobAnalysisResult> => {
    if (!keycloak?.token || !currentFirmId) {
      const error = new Error('Authentication required');
      handleError(error, 'analyzeFailedJob');
      throw error;
    }

    // Add job to analysis progress
    setState(prev => ({ 
      ...prev, 
      analysisInProgress: new Set([...prev.analysisInProgress, jobId]),
      error: null 
    }));

    try {
      const result = await analyzeJob({
        jobId,
        realmId: currentFirmId,
        analysisType: 'comprehensive',
        mcpStrategy: 'auto',
        ...options
      }, keycloak.token);

      // Update the job analysis data
      setState(prev => ({
        ...prev,
        mcpJobsData: {
          ...prev.mcpJobsData,
          [jobId]: {
            analysis: result,
            lastAnalyzed: new Date().toISOString()
          }
        },
        analysisInProgress: new Set([...prev.analysisInProgress].filter(id => id !== jobId)),
        lastUpdated: new Date().toISOString()
      }));

      return result;
    } catch (error) {
      // Remove from progress on error
      setState(prev => ({
        ...prev,
        analysisInProgress: new Set([...prev.analysisInProgress].filter(id => id !== jobId))
      }));
      handleError(error, 'analyzeFailedJob');
      throw error;
    }
  }, [keycloak?.token, currentFirmId, handleError]);

  const analyzeFailedJobs = useCallback(async (
    jobIds: string[], 
    options: Partial<BatchAnalysisRequest> = {}
  ): Promise<BatchAnalysisResult> => {
    if (!keycloak?.token || !currentFirmId) {
      const error = new Error('Authentication required');
      handleError(error, 'analyzeFailedJobs');
      throw error;
    }

    setState(prev => ({ 
      ...prev, 
      batchAnalysisInProgress: true,
      error: null 
    }));

    try {
      const result = await analyzeJobsBatch({
        jobIds,
        realmId: currentFirmId,
        analysisType: 'comprehensive',
        mcpStrategy: 'auto',
        ...options
      }, keycloak.token);

      // Update analysis data for all jobs
      const timestamp = new Date().toISOString();
      setState(prev => ({
        ...prev,
        mcpJobsData: {
          ...prev.mcpJobsData,
          ...Object.entries(result.results).reduce((acc, [jobId, analysis]) => {
            acc[jobId] = {
              analysis,
              lastAnalyzed: timestamp
            };
            return acc;
          }, {} as Record<string, McpJobData>)
        },
        batchAnalysisInProgress: false,
        lastUpdated: timestamp
      }));

      return result;
    } catch (error) {
      setState(prev => ({ ...prev, batchAnalysisInProgress: false }));
      handleError(error, 'analyzeFailedJobs');
      throw error;
    }
  }, [keycloak?.token, currentFirmId, handleError]);

  const refreshAnalysisData = useCallback(async (jobIds: string[]) => {
    if (!keycloak?.token) {
      handleError(new Error('Authentication required'), 'refreshAnalysisData');
      return;
    }

    try {
      const mcpData = await getMcpJobData(jobIds, keycloak.token);
      
      setState(prev => ({
        ...prev,
        mcpJobsData: {
          ...prev.mcpJobsData,
          ...mcpData
        },
        lastUpdated: new Date().toISOString()
      }));
    } catch (error) {
      handleError(error, 'refreshAnalysisData');
    }
  }, [keycloak?.token, handleError]);

  // ===== DATA ACCESS HELPERS =====

  const hasAnalysis = useCallback((jobId: string): boolean => {
    return Boolean(state.mcpJobsData[jobId]?.analysis);
  }, [state.mcpJobsData]);

  const getAnalysis = useCallback((jobId: string): JobAnalysisResult | null => {
    return state.mcpJobsData[jobId]?.analysis || null;
  }, [state.mcpJobsData]);

  const getAnalysisIndicator = useCallback((jobId: string): '🧠' | '✨' | '⚠️' | '❌' | null => {
    const analysis = state.mcpJobsData[jobId]?.analysis;
    if (!analysis) return null;

    switch (analysis.status) {
      case 'success':
        return '🧠';
      case 'warning':
        return '⚠️';
      case 'error':
        return '❌';
      default:
        return '✨';
    }
  }, [state.mcpJobsData]);

  // 🚀 NEW: Enhanced Data Accessors (Phase 2)
  
  const getLastAnalyzedAt = useCallback((jobId: string): string | null => {
    return state.mcpJobsData[jobId]?.lastAnalyzed || null;
  }, [state.mcpJobsData]);

  const getAnalysisStatus = useCallback((jobId: string): 'success' | 'warning' | 'error' | 'unknown' => {
    const analysis = state.mcpJobsData[jobId]?.analysis;
    if (!analysis) return 'unknown';
    
    return analysis.status || 'unknown';
  }, [state.mcpJobsData]);

  const getAnalysisSummary = useCallback((jobId: string): string | null => {
    const analysis = state.mcpJobsData[jobId]?.analysis;
    return analysis?.summary || null;
  }, [state.mcpJobsData]);

  // 🚀 BONUS: Staleness tracking helper (Phase 2)
  const isAnalysisStale = useCallback((jobId: string, maxAgeMinutes: number = 15): boolean => {
    const lastAnalyzed = getLastAnalyzedAt(jobId);
    if (!lastAnalyzed) return false;
    
    const ageMs = Date.now() - new Date(lastAnalyzed).getTime();
    const ageMinutes = ageMs / (1000 * 60);
    return ageMinutes > maxAgeMinutes;
  }, [getLastAnalyzedAt]);

  // ===== EFFECTS =====

  // 🚀 ENHANCED: Auto-refresh with interval ref to prevent memory leaks (Phase 2)
  useEffect(() => {
    // Clear any existing interval
    if (autoRefreshIntervalRef.current) {
      clearInterval(autoRefreshIntervalRef.current);
      autoRefreshIntervalRef.current = null;
    }

    const jobsWithAnalysis = Object.keys(state.mcpJobsData).filter(jobId => 
      state.mcpJobsData[jobId]?.analysis
    );

    if (jobsWithAnalysis.length === 0) return;

    // Set new interval with ref tracking
    autoRefreshIntervalRef.current = setInterval(() => {
      refreshAnalysisData(jobsWithAnalysis);
    }, 5 * 60 * 1000); // 5 minutes

    // Cleanup function
    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
        autoRefreshIntervalRef.current = null;
      }
    };
  }, [state.mcpJobsData, refreshAnalysisData]);

  // ===== RETURN INTERFACE =====

  return {
    // State
    mcpJobs: state.mcpJobsData,
    isAnalyzing: state.analysisInProgress.size > 0,
    isBatchAnalyzing: state.batchAnalysisInProgress,
    error: state.error,
    
    // Analysis Operations
    analyzeFailedJob,
    analyzeFailedJobs,
    refreshAnalysisData,
    
    // Data Access
    hasAnalysis,
    getAnalysis,
    getAnalysisIndicator,
    
    // 🚀 NEW: Enhanced Data Accessors (Phase 2)
    getLastAnalyzedAt,
    getAnalysisStatus,
    getAnalysisSummary,
    isAnalysisStale,
    
    // Utilities
    clearError
  };
}

/**
 * Helper hook to get analysis status for multiple jobs at once
 */
export function useMcpJobAnalysisFilter() {
  const { mcpJobs, hasAnalysis } = useMcpJobAnalysis();
  
  const getAnalyzedJobIds = useCallback((): string[] => {
    return Object.keys(mcpJobs).filter(jobId => hasAnalysis(jobId));
  }, [mcpJobs, hasAnalysis]);

  const getAnalysisCount = useCallback((): number => {
    return getAnalyzedJobIds().length;
  }, [getAnalyzedJobIds]);

  const filterJobsByAnalysisStatus = useCallback((
    jobs: Array<{id: string}>, 
    status?: 'success' | 'warning' | 'error'
  ) => {
    if (!status) {
      return jobs.filter(job => hasAnalysis(job.id));
    }
    
    return jobs.filter(job => {
      const analysis = mcpJobs[job.id]?.analysis;
      return analysis && analysis.status === status;
    });
  }, [mcpJobs, hasAnalysis]);

  return {
    getAnalyzedJobIds,
    getAnalysisCount,
    filterJobsByAnalysisStatus
  };
}
